import { X } from 'lucide-react'
import { useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'

interface TagInputProps {
  value: string[]
  onChange: (tags: string[]) => void
  placeholder?: string
  id?: string
  className?: string
  /** Existing tags to type-ahead against. Tags are free text, so a typo makes
   *  a silent second tag — matching an existing one should be the easy path. */
  suggestions?: string[]
}

const MAX_VISIBLE_SUGGESTIONS = 8

/** Chip-style tag editor: Enter or comma commits a tag, Backspace on an empty
 *  input removes the last one, × removes a specific tag. Pending text is also
 *  committed on blur so a half-typed tag isn't lost on save.
 *
 *  With `suggestions`, typing filters the existing tags into a dropdown:
 *  ↑/↓ moves, Enter takes the highlighted one (or commits what you typed when
 *  nothing is highlighted), Escape dismisses the list. */
export function TagInput({ value, onChange, placeholder = 'Add a tag…', id, className, suggestions = [] }: TagInputProps) {
  const [draft, setDraft] = useState('')
  const [highlighted, setHighlighted] = useState(-1)
  const [dismissed, setDismissed] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const matches = useMemo(() => {
    const query = draft.trim().toLowerCase()
    return suggestions
      .filter(tag => !value.includes(tag))
      .filter(tag => (query ? tag.toLowerCase().includes(query) : true))
      .slice(0, MAX_VISIBLE_SUGGESTIONS)
  }, [suggestions, value, draft])

  const open = !dismissed && matches.length > 0

  const commit = (raw: string) => {
    const tag = raw.trim()
    if (!tag) return
    if (!value.includes(tag)) onChange([ ...value, tag ])
    setDraft('')
    setHighlighted(-1)
  }

  const remove = (tag: string) => onChange(value.filter(t => t !== tag))

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault()
      const step = e.key === 'ArrowDown' ? 1 : -1
      setHighlighted(prev => {
        const next = prev + step
        if (next < 0) return matches.length - 1
        if (next >= matches.length) return 0
        return next
      })
    } else if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit(open && highlighted >= 0 ? matches[highlighted] : draft)
    } else if (e.key === 'Escape' && open) {
      e.preventDefault()
      setDismissed(true)
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      remove(value[value.length - 1])
    }
  }

  return (
    <div className="relative">
      <div
        className={cn(
          'flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm',
          'focus-within:outline-none focus-within:ring-1 focus-within:ring-ring cursor-text',
          className,
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map(tag => (
          <Badge key={tag} variant="secondary" className="gap-1 pr-1">
            {tag}
            <button
              type="button"
              aria-label={`Remove ${tag}`}
              className="inline-flex size-4 cursor-pointer items-center justify-center rounded-full hover:bg-muted-foreground/20"
              onClick={() => remove(tag)}
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
        <input
          id={id}
          ref={inputRef}
          value={draft}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          onChange={e => { setDraft(e.target.value); setHighlighted(-1); setDismissed(false) }}
          onKeyDown={onKeyDown}
          onFocus={() => setDismissed(false)}
          // Blur commits any half-typed text, but must not fire before a click
          // on a suggestion lands — hence onMouseDown on the options below.
          onBlur={() => { commit(draft); setDismissed(true) }}
          placeholder={value.length === 0 ? placeholder : ''}
          className="min-w-24 flex-1 appearance-none border-0 bg-transparent py-0.5 text-sm shadow-none outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none placeholder:text-muted-foreground"
        />
      </div>

      {open && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md"
        >
          {matches.map((tag, index) => (
            <li key={tag}>
              <button
                type="button"
                role="option"
                aria-selected={index === highlighted}
                onMouseDown={e => { e.preventDefault(); commit(tag) }}
                onMouseEnter={() => setHighlighted(index)}
                className={cn(
                  'w-full cursor-pointer rounded-sm px-2 py-1.5 text-left text-sm transition-colors',
                  index === highlighted ? 'bg-accent text-accent-foreground' : 'text-foreground',
                )}
              >
                {tag}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
